import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Table, Alert, Checkbox, Button, message, Tooltip, Icon } from 'antd';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { formatPriceWithSpace } from '~helpers/currency';
import { PropertyCountry as PropertyCountryIcon } from 'common/components/svgs';
import { dayFormatType, formatDay } from '~helpers/day';
import classNames from 'classnames';
import { QUERY_PRE_CAL_COMMISSION } from './queries';

export default function ReviewCommission(props) {
  const { action, commissionTier, properties, onConfirm, onClose, displayType = 'modal' } = props;
  const { t } = useTranslation();
  const client = useApolloClient();

  const [showAllColumns, setShowAllColumns] = useState(false);
  const [fetchPreCommissionLoading, setFetchPreCommissionLoading] = useState(false);
  const [preCommissionData, setPreCommissionData] = useState({ bookingData: [], orderData: [] });

  const modifyCommissionTier = useMemo(() => {
    const modifyCommissionTier = { ...commissionTier };
    delete modifyCommissionTier.name;
    delete modifyCommissionTier.unitTypeIds;
    delete modifyCommissionTier.propertyId;
    delete modifyCommissionTier.description;
    return modifyCommissionTier;
  }, [commissionTier]);

  const bulkFetchPreCommission = useCallback(async (action, commissionTier, properties) => {
    setFetchPreCommissionLoading(true);
    const promises = properties.map(property =>
      client.query({
        query: QUERY_PRE_CAL_COMMISSION,
        variables: {
          action: action,
          propertyId: property.id,
          commissionTier: commissionTier,
        },
        fetchPolicy: 'no-cache',
      }),
    );
    try {
      const responses = await Promise.all(promises);
      let bookingData = [];
      let orderData = [];
      responses.forEach(response => {
        bookingData = bookingData.concat(
          response?.data?.preCalCommissionWhenPropertyTierChanged?.bookingData || [],
        );
        orderData = orderData.concat(
          response?.data?.preCalCommissionWhenPropertyTierChanged?.orderData || [],
        );
      });

      setPreCommissionData({ bookingData, orderData });
    } catch (error) {
      message.error(error.toString());
    } finally {
      setFetchPreCommissionLoading(false);
    }
  }, []);

  useEffect(() => {
    bulkFetchPreCommission(action, modifyCommissionTier, properties);
  }, []);

  const tableData = useMemo(() => {
    const formattedOrderData = preCommissionData.orderData.map(order => ({
      ...order,
      booking: {
        ...order.booking,
        opportunity: {
          referenceId: order.booking.commissions[0]?.referenceId,
        },
        commissionData: order.booking.commissions[0]?.data?.commission?.commissionData,
        bonusCommissionValue: order.booking.commissions[0]?.bonusPrice,
        totalCommissionValue: order.booking.commissions[0]?.totalPrice,
      },
    }));

    return (
      preCommissionData?.bookingData
        ?.concat(formattedOrderData)
        .map(item => {
          return {
            ...item,
            isSameWithOldCommission:
              item.booking.totalCommissionValue === item.commission.totalCommissionValue,
          };
        })
        .sort((a, b) =>
          Date.parse(a.booking.completedAt) < Date.parse(b.booking.completedAt) ? 1 : -1,
        ) || []
    );
  }, [preCommissionData]);

  const tableColumns = useMemo(() => {
    return [
      {
        title: t('cms.property.commission.preview.table.opportunity_name'),
        key: 'Opportunity Name',
        className: 'property-review-list__table__column-title--w-full',
        width: showAllColumns ? 200 : 164,
        fixed: showAllColumns ? 'left' : null,
        render: (_text, record) => {
          return record?.booking.opportunity?.name || '-';
        },
      },
      {
        title: t('cms.property.commission.preview.table.opportunity_num'),
        key: 'Opportunity Number',
        className: 'property-review-list__table__column-title--w-full',
        width: showAllColumns ? null : 128,
        render: (_text, record) => {
          return record?.booking.opportunity?.referenceId || '-';
        },
      },
      {
        title: t('cms.property.commission.preview.table.completed_date'),
        key: 'completed_date',
        className: 'property-review-list__table__column-title--w-full',
        width: 128,
        render: (_text, record) => {
          return formatDay(dayFormatType.UKWITHHMS, record?.booking.completedAt) || '-';
        },
      },
      ...(showAllColumns
        ? [
            {
              title: t('cms.property.commission.preview.table.old_commission_plan'),
              key: 'Old Commission Plan',
              className: 'property-review-list__table__column-title--w-full',
              width: 165,
              render: (_text, record) => {
                return record?.booking?.commissionData?.tier?.name || '-';
              },
            },
          ]
        : []),
      {
        title: t('cms.property.commission.preview.table.old_commission'),
        key: 'old_commission',
        className: 'property-review-list__table__column-title--w-full',
        width: 110,
        render: (_text, record) => {
          return record?.booking.totalCommissionValue !== null
            ? formatPriceWithSpace(record?.booking.totalCommissionValue, record?.booking.currency)
            : '-';
        },
      },
      ...(showAllColumns
        ? [
            {
              title: t('cms.property.commission.preview.table.new_commission_plan'),
              key: 'New Commission Plan',
              className: 'property-review-list__table__column-title--w-full',
              width: 165,
              render: (_text, record) => {
                return record?.commission.commissionData?.tier?.name || '-';
              },
            },
          ]
        : []),
      {
        title: t('cms.property.commission.preview.table.new_commission'),
        key: 'New Commission',
        className: 'property-review-list__table__column-title--w-full',
        width: showAllColumns ? 122 : 110,
        render: (_text, record) => {
          return (
            <span>
              {record?.commission.totalCommissionValue !== null
                ? formatPriceWithSpace(
                    record?.commission.totalCommissionValue,
                    record?.booking.currency,
                  )
                : '-'}

              {!record.isSameWithOldCommission && (
                <Tooltip
                  arrowPointAtCenter
                  placement="topRight"
                  title="It’s different with old commission."
                >
                  <Icon
                    className="property-review-list__table__tooltip-icon"
                    type="info-circle"
                    theme="filled"
                  />
                </Tooltip>
              )}
            </span>
          );
        },
      },
    ];
  }, [showAllColumns]);

  const onContinue = useCallback(() => {
    onConfirm();
  }, []);
  const onBack = useCallback(() => {
    onClose();
  }, []);

  return (
    <>
      <div className="review-commission-modal__body">
        <section>
          <If condition={displayType === 'modal'}>
            <h2>
              <PropertyCountryIcon className="commission-from__country-icon" />
              <span>{properties.length === 1 ? properties[0]?.name : 'Multi Property'}</span>
            </h2>
          </If>

          <h3 className="review-commission-modal__body__section-title">
            {t('cms.properties.edit.commission.preview.tip.title')}
          </h3>
          <p>{t('cms.properties.edit.commission.preview.tip.context')}</p>
          <Alert
            className="review-commission-modal__alert"
            message={t('cms.properties.edit.commission.preview.alert.context')}
            banner
          />

          <div className="property-review-list__table">
            <div className="table-list__header">
              <span>
                {t('cms.properties.edit.commission.preview.table.total', {
                  total: tableData.length,
                })}
              </span>
              <Checkbox
                className={classNames('table-list__header__checkbox', {
                  'ant-checkbox-wrapper--unselect': !showAllColumns,
                })}
                checked={showAllColumns}
                onChange={e => setShowAllColumns(e.target.checked)}
              >
                {t('cms.properties.edit.commission.preview.table.show_all')}
              </Checkbox>
            </div>
            <Table
              columns={tableColumns}
              loading={fetchPreCommissionLoading}
              className="table-container"
              dataSource={tableData}
              pagination={false}
              scroll={{ x: true, y: tableData.length > 4 ? 300 : false }}
              rowKey="id"
              rowClassName={record => {
                if (!record.isSameWithOldCommission) {
                  return 'property-review-list__table__row--alert';
                }
              }}
            />
          </div>
        </section>
      </div>
      <If condition={displayType === 'modal'}>
        <footer className="review-commission-modal__footer">
          <Button
            ghost
            type="primary"
            size="large"
            className="review-commission-modal__cancel-button"
            onClick={onBack}
          >
            {t('cms.referral.commission_plan.modal.btn.cancel')}
          </Button>

          <Button
            type="primary"
            size="large"
            className="review-commission-modal__save-button"
            onClick={onContinue}
          >
            {t('cms.special_offer.confirm.btn')}
          </Button>
        </footer>
      </If>
    </>
  );
}
